import type { BadgeTone } from '@wundo/ui';
import type {
  NotificationChannel,
  SendableNotificationChannel,
} from '@wundo/shared/notificationChannel.ts';
import type {
  NotificationDelivery,
  NotificationRecipient,
  NotificationSkipReason,
} from '@/lib/notificationSend.ts';

/** Why somebody cannot be reached on the channel the send goes on. */
const UNREACHABLE_REASONS: ReadonlySet<NotificationSkipReason> = new Set([
  'no_teams_chat',
  'no_slack_chat',
  'no_webapp',
  'teams_only',
  'teams_or_email_only',
  'no_email_address',
  'opted_out',
  'channel_none',
  'magic_link_disabled',
  'expired',
]);

/** Named before anybody presses the button, so the list warns about exactly who a send skips. */
export interface SendReach {
  skipReasonOf: (person: NotificationRecipient) => NotificationSkipReason | null;
  /** Where a row's send would go: the mail's one channel, the chosen one, or their own. */
  channelOf: (person: NotificationRecipient) => NotificationChannel;
  channelTone: (person: NotificationRecipient) => BadgeTone;
  leftOut: NotificationRecipient[];
  selectedLeftOutReasons: NotificationSkipReason[];
  reachableCount: number;
  /** Who a resend reaches a second time. */
  resentCount: number;
}

interface SendReachInput {
  people: NotificationRecipient[];
  selected: NotificationRecipient[];
  previewRecipient: NotificationRecipient | null;
  /** The delivery the preview drew, for the one person it was drawn for. */
  delivery: NotificationDelivery | undefined;
  isEmail: boolean;
  channel: SendableNotificationChannel | undefined;
  resend: boolean;
}

export function sendReach({
  people,
  selected,
  previewRecipient,
  delivery,
  isEmail,
  channel,
  resend,
}: SendReachInput): SendReach {
  const isPreviewed = (person: NotificationRecipient): boolean =>
    person.user_id === previewRecipient?.user_id;

  /* A list says so per row; one name picked by hand comes without it, and its preview knows. */
  const skipReasonOf = (person: NotificationRecipient): NotificationSkipReason | null =>
    person.skip_reason ?? (isPreviewed(person) ? (delivery?.skip_reason ?? null) : null);

  const unreachable = (person: NotificationRecipient): boolean => {
    const reason = skipReasonOf(person);
    return reason !== null && UNREACHABLE_REASONS.has(reason);
  };

  const alreadyReceived = (person: NotificationRecipient): boolean =>
    person.sent_at !== null || (isPreviewed(person) && delivery?.settled === true);

  const channelOf = (person: NotificationRecipient): NotificationChannel =>
    isEmail ? 'email' : (channel ?? person.notification_channel);

  const channelTone = (person: NotificationRecipient): BadgeTone => {
    if (unreachable(person)) return 'danger';
    return channelOf(person) === 'none' ? 'neutral' : 'info';
  };

  const selectedLeftOutReasons = selected
    .map(skipReasonOf)
    .filter((reason) => reason !== null);

  return {
    skipReasonOf,
    channelOf,
    channelTone,
    leftOut: people.filter((person) => skipReasonOf(person) !== null),
    selectedLeftOutReasons,
    reachableCount: selected.length - selectedLeftOutReasons.length,
    resentCount: resend
      ? selected.filter(
          (person) => skipReasonOf(person) === null && alreadyReceived(person),
        ).length
      : 0,
  };
}
